import type { Distance, Point, SpatialRectangle } from "@pdfking/math"
import type { Margins } from "@pdfking/math"
import { RotateMarginsLeft, RotateMarginsRight } from "@pdfking/math"

import type { InternalCoord } from "../types/coord"
import type { FillNodeData } from "../types/node"
import { internal_matrix } from "./InternalMatrix"

/**
 * Mirrors Python `FillStrategy._internal_coords()` (strategy.py:1184-1219):
 *
 *   for rotated, matrix, offset, margins in self._internal_matrix:
 *       for x in range(matrix.x):
 *           for y in range(matrix.y):
 *               s = XYMatrix(x=rotated and inner.height or inner.width,
 *                            y=rotated and inner.width or inner.height)
 *               m = self.padding
 *               b = MarginMeasureSet(**{
 *                   attr: check and margins[attr] or D()
 *                   for attr, check in (
 *                       ("margin_left", x == 0),
 *                       ("margin_top", y == 0),
 *                       ("margin_right", (x + 1) == matrix.x),
 *                       ("margin_bottom", (y + 1) == matrix.y),
 *                   )
 *               })
 *               if rotated:
 *                   m = m.rotate_fields()
 *                   b = b.rotate_fields(reverse=True)
 *               yield (rotated,
 *                      XYMatrix(
 *                          x=offset.x + m.margin_left + (s.x + m.margin_right + m.margin_left) * x,
 *                          y=offset.y + m.margin_top + (s.y + m.margin_top + m.margin_bottom) * y,
 *                      ),
 *                      b)
 *
 * Walks every subject cell of every matrix block, computing its
 * absolute position and which of its edges face the empty sheet (only
 * grid edges can).
 */
export const internal_coords = (
  data: FillNodeData,
  position: SpatialRectangle<Distance>
): InternalCoord[] => {
  const inner_w = data.inner.width
  const inner_h = data.inner.height
  const out: InternalCoord[] = []
  for (const block of internal_matrix(data, position)) {
    const { rotated, matrix, offset, open } = block
    // Python: s = XYMatrix(x=rotated and inner.height or inner.width, ...)
    const s_x: Distance = rotated ? inner_h : inner_w
    const s_y: Distance = rotated ? inner_w : inner_h
    // Python: m = self.padding; if rotated: m = m.rotate_fields()
    const m: Margins<Distance> = rotated ? RotateMarginsLeft(data.padding) : data.padding
    for (let x = 0; x < matrix.x; x++) {
      for (let y = 0; y < matrix.y; y++) {
        // Python: b = MarginMeasureSet per-edge based on grid position.
        const b_left = x === 0 && open.left
        const b_top = y === 0 && open.top
        const b_right = x + 1 === matrix.x && open.right
        const b_bottom = y + 1 === matrix.y && open.bottom
        const b: Margins<boolean> = rotated
          ? RotateMarginsRight({
              top: b_top,
              right: b_right,
              bottom: b_bottom,
              left: b_left,
            })
          : {
              top: b_top,
              right: b_right,
              bottom: b_bottom,
              left: b_left,
            }
        // Python: offset.x + m.margin_left + (s.x + m.margin_right + m.margin_left) * x
        //
        // `y` counts rows from the TOP (row 0 is the first page), while the
        // coordinate grows upward, so the row index is measured back from the
        // last row. Everything else — which edges carry bleed, the order the
        // cells come out in, and therefore the page numbering — is untouched.
        const rows_below = matrix.y - 1 - y
        const coord: Point<Distance> = {
          x: offset.x + m.left + (s_x + m.right + m.left) * x,
          y: offset.y + m.bottom + (s_y + m.top + m.bottom) * rows_below,
        } as Point<Distance>
        out.push({ rotated, coord, open: b })
      }
    }
  }
  return out
}
